import React, { useEffect, useReducer, useState } from 'react';
import { FormattedMessage, injectIntl} from 'react-intl';
import { Route, HashRouter as Router, Switch, useHistory } from 'react-router-dom';
import { getRemotedbUrl, i18nIntl, isNotEmpty, NSchange } from '../../lib/ep.js';
import { stage_categories, stage_promotion_types } from '../../data/bootstrap_stages';
import { useCSS } from '../../lib/hooks.js';
import { useOwner } from '../context/context.js';
import { operators, useFilterDataQuery, usePaginator } from '../FilterContent.js';

import { CreateButton, MainButton, SecondaryButton, SquaredButton } from '../builder/ActionButton.js';
import ActionLink from '../builder/ActionLink.js';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain, { BuilderMainWithoutSidebar } from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox';
import ColorPairsSelector from '../builder/ColorPairsSelector';
import { DeleteButton } from '../builder/ConfirmButton.js';
import CountryField2 from '../builder/CountryField2.js';
import DynamicFields, { TranslationField } from '../builder/DynamicFields.js';
import DynamicForm from '../builder/DynamicForm';
import FieldLabel from '../builder/FieldLabel.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import Input from '../builder/Input';
import MediaUploader from '../builder/MediaUploader';
import { InformationMessage } from '../builder/Message';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Range from '../builder/Range';
import RichEditor from '../builder/RichEditor';
import SaveButton from '../builder/SaveButton.js';
import SearchField from '../builder/SearchField.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import SectionTitle from '../builder/SectionTitle.js';
import Select from '../builder/Select';
import SelectRibbon from '../builder/SelectRibbon.js';
import TextArea from '../builder/TextArea.js';
import Tooltip from '../builder/Tooltip.js';
import UrlField from '../builder/UrlField.js';

import Layout from '../front/Layout';
import MediaParticipation from '../front/MediaParticipation';
import SingleGallery from '../front/SingleGallery.js';
import { UploadAvatar } from '../front/UserAvatar.js';

import CandidateManager from '../users/CandidateManager.js';

import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import SingleRegisteredEntry from './SingleRegisteredEntry.js';
import ExportModal from './ExportModal.js';
import { InnerComment } from './MultiMediaTool.js';

import Paginator from '../utils/Paginator.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import ItemAsRibbons from '../builder/ItemAsRibbons.js';
import ToggleCollection from '../builder/ToggleCollection.js';
import ColorPicker from '../builder/ColorPicker.js';
import { defaults } from '../promotion/bootstrap.js';
import RangeDouble from '../builder/RangeDouble.js';

const InputPlaceHolder = injectIntl(transferPlaceholder(Input));
const RichPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

const TagField = ({ model, change, ...props }) => {
	return (
		<Model
			model={model}
			change={change}
			errors={props.errors != undefined && props.errors[model.id]}
		>
			<Input
				name='title'
				max_length={30}
				_class={{ size:50 }}
			>Title</Input>

			<ColorPicker
				name="color"
				_class={{ size:50 }}
				_hide_code={true}
				color={model?.color ?? 'rgba(0, 0, 0, 1)'}
			>Color</ColorPicker>
		</Model>
	)
};

const MediaTagsPicker = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ selected_tags, setSelectedTags ] = useState(model?.media_tags ?? []);
	const [ display_create_layer, setDisplayCreateLayer ] = useState(false);
	const [ errors, setErrors ] = useState({});

	const max_tags = 25;
	
	const tags = props.pm.attributes?.available_media_tags ?? [];
	const [ tmp_tags, setTmpTags ] = useState(tags);
	const enable_add = props?.enable_add ?? true;

	const toggleTag = (tag_id) => {
		let _selected_tags = [ ...selected_tags ];
		
		if(selected_tags.includes(tag_id)) {
			_selected_tags = _selected_tags.filter((t) => (t != tag_id));
		} else {
			_selected_tags.push(tag_id);
		}

		setSelectedTags(_selected_tags);
		change('media_tags', _selected_tags);
	};

	const usable_tags = [];
	const media_tags_in_stages = (props?.stages ?? []).filter((s) => s.Stage.type == stage_categories.ID_MEDIA_CATEGORY.id);
	const unique_tags = [
		...new Set(media_tags_in_stages.flatMap((item) => item.Stage.media_tags)),
	];

	tmp_tags.sort((a, b) => {
		return (a.title < b.title) ? -1 : 1;
	}).map((t) => {
		const disable_delete = unique_tags.includes(t.id);
		usable_tags.push({
			...t,
			disable_delete,
			delete_tooltip:(disable_delete) ? "This tag is being used and cannot be deleted" : null,
		});
	});

	const tag_fields = [{
		type:'tag',
		label:"Tag",
		icon:'ep-circle',
		translate:[{
			key:'title',
			type:'input',
		}],
		title:(f) => {
			return (isNotEmpty(f, 'title')) ? f.title : `Tag`;
		},
		titleStyles:(f) => {
			return (isNotEmpty(f, 'color')) ? { '--icon-color':f.color } : null;
		},
	}];

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		});
	}

	const validate = (v) => {
		v = (v == undefined) ? tags : v;
		let errors = {};

		v.map((tag) => {
		
			if(errors[tag.id] == undefined) errors[tag.id] = {};
			
			if(tag.title == undefined || tag.title == '') {
				errors[tag.id] = i18n("Mandatory tag");
			}

			if(tag.title != undefined && tag.title.length > 0){
				let already_tag = v.find((word) => {
					return (word.id != tag.id && word.title != undefined && (word.title).toLowerCase() == (tag.title).toLowerCase()) ? true : false;
				});
				if(already_tag) errors[tag.id] =  i18n("Repeated tag");
			}
			
			if(Object.keys(errors[tag.id]).length == 0) delete errors[tag.id];
		});
		setErrors(errors);
		return Object.keys(errors).length == 0;
	};

	useEffect(() => {
		validate(tmp_tags);
	},[tmp_tags])

	useEffect(() => {
		setTmpTags(tags);
	},[display_create_layer])

	const promoChange = (k, v, cb) => {
		if(k == 'available_media_tags') {
			const delete_fields = ['disable_delete', 'delete_tooltip','error'];
			v = JSON.parse(JSON.stringify(v));	// amazingly smart way to deep clone an object to avoid removing the delete_fields
			v = v.map((field) => {
				delete_fields.map((k) => {
					delete field[k];
				});
				return field;
			});
		}
		NSchange(props.pm.attributes, k, v);
		setTimeout(() => {
			if(cb!=undefined)cb();
		}, 300);
	};

	const saveTags = (e, cb) => {
		promoChange('available_media_tags',tmp_tags,()=>{
			props.pm.save();
			if(cb != undefined) cb();
		})
	};

	const total_selected = tags.filter((item) => selected_tags.includes(item.id)).length;
	const max_tags_min = Math.max((model?.media_min_tags ?? 1), 1);
	const max_tags_max = Math.max(total_selected, 1);

	const message_label = (total_selected == 0) ? "There are no {label} selected" : ((total_selected == 1) ? "{total} {label} selected" : "{total} {label} are selected");
	const label = (total_selected == 1) ? "Tag" : "Tags";

	const disabled_save = Object.keys(errors).length > 0 ? true : false;
	const tags_section_title = `${i18n("Tags")} ${(usable_tags.length > 0) ? ` (${usable_tags.length}/${max_tags})` : ''}`;

	return (
		<article class={(props.is_media_autocontent === false) ? "gui_not_in_top_huge" : null}>
			<BuilderSubSectionTitle
				title="Tags"
				subtitle="Participants can add tags to organize content into categories. They can then filter the gallery by these tags to quickly find what they're looking for."
				icon='knk-tag'
				title_actions={(enable_add) ? (
					<ButtonCollection>
						<MainButton
							onClick={() => setDisplayCreateLayer(true)}
						>Manage tags</MainButton>
					</ButtonCollection>
	 			) : null}
			/>

			<div class="gui_not_in_top_small">
				{(tags.length > 0) && (
					<ToggleCollection
						items={tags.filter((item) => {return isNotEmpty(item,'title',[''])})}
						onToggle={toggleTag}
						selected={selected_tags}
						filter_by={['title']}
					>Click to select the tags</ToggleCollection>
				)}

				{(tags.length > 0) && (
					<p class="gui_not_in_top_small">
						{props.intl.formatMessage({
							id:message_label,
						}, {
							total:total_selected,
							label:`${i18n(label, 'lowercase')}`,
						})}
					</p>
				)}

				{(tags.length > 0 && total_selected > 0) && (
					<Model
						model={model}
						change={change}
					>
						<Range
							name='media_min_tags'
							min={1}
							max={Math.max(selected_tags.length - 1, 1)}
							value={model?.media_min_tags ?? 1}
							default={1}
							step={1}
							_class={{ size:60 }}
						>Min. tags to select</Range>

						<Range
							name='media_max_tags'
							min={max_tags_min}
							max={max_tags_max}
							value={Math.min(max_tags_max, Math.max(max_tags_min, (model?.media_max_tags ?? (model?.media_min_tags ?? 1))))}
							default={1}
							step={1}
							_class={{ size:60, first:true }}
						>Max. tags to select</Range>

						<Input
							name='media_tags_label'
							value={model?.media_tags_label ?? i18n("Select a tag")}
							_class={{ size:60 }}
						>Tag selector text</Input>
					</Model>
				)}

				{(display_create_layer) && (
					<FloatingLayer
						_class={{ size:'small' }}
						backButton={() => setDisplayCreateLayer(false)}
					>
						<div>
							<DynamicFields
								section_title={tags_section_title}
								onChange={(v) => setTmpTags(v)}
								field_types={tag_fields}
								has_translation={true}
								sortable={false}
								can_copy={false}
								locales={avail_locales}
								max_items={max_tags}
								add_button_props={{
									icon:'knk-add-item mainButton',
								}}
								ul_classes='gui_overflowable'
								ul_styles={{ maxHeight:'calc(100dvh - 285px)' }}
								fields={usable_tags}
								nameAutoTransform={(v) => {
									return UCFirst(v.replace(/[\-\_]/gi, " "));
								}}
								errors={errors}
							>
								<TagField />
							</DynamicFields>

							<ButtonCollection
								in_floating={true}
							>
								<SaveButton
									disabled = {disabled_save}
									onClick = {saveTags}
								>Save changes</SaveButton>

								<SecondaryButton
									onClick={() => setDisplayCreateLayer(false)}
								>Close</SecondaryButton>
							</ButtonCollection>
						</div>
					</FloatingLayer>
				)}
			</div>
		</article>
	)
};

const MediaSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const count_default_sizes = 10;
	let sizes_file = [];
	for(let i = 1; i < count_default_sizes + 1; i++) sizes_file.push({ k:i, v:`${i} MB` });
	sizes_file.push({ k:'20', v:'20 MB' });
	sizes_file.push({ k:'50', v:'50 MB' });
	sizes_file.push({ k:'100', v:'100 MB' });
	sizes_file.push({ k:'150', v:'150 MB' });
	sizes_file.push({ k:'250', v:'250 MB' });
	sizes_file.push({ k:'500', v:'500 MB' });

	const [errors,setErrors] = useState({});
	const [errorsMinMax,setErrorsMinMax] = useState({});
	const [hashtag_IG,setHashtagIG] = useState('');
	const [hashtag_TT,setHashtagTT] = useState('');
	
	const max_elements_to_upload = 5;
	const media_min_elements = stage?.media_min_elements ?? 1;
	const media_max_elements = stage?.media_max_elements ?? 2;
	
	const image_permitted = stage?.media_image ?? false;
	const video_permitted = stage?.media_video ?? false;
	const pdf_permitted = stage?.media_pdf ?? false;
	const youtube_permitted = stage?.media_video_url ?? false;
	const text_permitted = stage?.media_text ?? false;
	const url_permitted = stage?.media_url ?? false;
	const uploaded_permitted = (image_permitted || video_permitted || pdf_permitted) ? true : false;
	const is_only_text = ((text_permitted || url_permitted) && !(uploaded_permitted || youtube_permitted)) ? true : false;
	
	const default_description_text = `<h1>${i18n("Submit your entry")}</h1><p>${i18n("Complete all the fields, and remember that you can enter emojis to make your entry stand out and shine.")}</p>`;
	
	//const hashtags_ig = stage?.media_instagram_hashtags ?? [];//OUT Basic Display Instagram 4/12/24
	const hashtags_ig = [];
	const hashtags_tt = stage?.media_tiktok_hashtags ?? [];

	const addHashtag = (hashtag, network) => {
		const field = `media_${network}_hashtags`
		const _hashtag = hashtag.toLowerCase();
		const hashtagRegex = /#[\p{L}\p{N}_]+/gu;//ALL Langs added => /#[A-Za-z0-9]+/;
		const hashtagRegexMultiAndSpaces = /^#[\p{L}\p{N}_]+$/u;//ALL Langs added => /#[A-Za-z0-9]+/;

		const good = hashtagRegex.test(_hashtag);
		const good2 = hashtagRegexMultiAndSpaces.test(_hashtag);

		let _hashtags = [];
		if(network == 'instagram') _hashtags = hashtags_ig;
		if(network == 'tiktok') _hashtags = hashtags_tt;

		let _errors = {};
		if(good && good2){
			if(!_hashtags.includes(_hashtag)){
				_hashtags.push(_hashtag);
				change(field,_hashtags);
				if(network == 'instagram')setHashtagIG('');
				if(network == 'tiktok')setHashtagTT('');
			}else{
				_errors[`hashtag_${network}`] = i18n('This hashtag already exists');
			}
		} else if(!good) {
			_errors[`hashtag_${network}`] = i18n('Hashtag must include the # symbol');
		} else if(!good2) {
			_errors[`hashtag_${network}`] = props.intl.formatMessage({ id:"{string} is not a valid hashtag" }, { string:`"${hashtag}"`});
		}
		setErrors(_errors);
	};

	const removeHashtag = (index,network) => {
		const field = `media_${network}_hashtags`
		let _hashtags = [];
		if(network == 'instagram') _hashtags = hashtags_ig;
		else if(network == 'tiktok') _hashtags = hashtags_tt;

		const to_remove = _hashtags[index];
		const new_medias = _hashtags.filter(d=>(d!=to_remove));
		change(field, new_medias.length == 0 ? null : new_medias );
	};

	const changeMinMax = (k,v) =>{
		let _errors_min_max = {};

		const media_text_min = parseInt(stage?.media_text_min ?? 0);
		const media_text_max = parseInt(stage?.media_text_max ?? 1500);
		const media_caption_min = parseInt(stage?.media_caption_min ?? 0);
		const media_caption_max = parseInt(stage?.media_caption_max ?? 1500);
		
		if(k == 'media_text_min' && parseInt(v) > media_text_max){
			_errors_min_max[k] = 1;
		}else if(k == 'media_text_max' && parseInt(v) < media_text_min){
			_errors_min_max[k] = 1;
		}else if(k == 'media_caption_min' && parseInt(v) > media_caption_max){
			_errors_min_max[k] = 1;
		}else if(k == 'media_caption_max' && parseInt(v) < media_caption_min){
			_errors_min_max[k] = 1;
		}
		setErrorsMinMax(_errors_min_max);
		if(!_errors_min_max[k])change(k,v);
	}
	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={{ ...errors, ...errorsMinMax }}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={props.nav_active}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<BuilderSubSectionTitle
						title="Multimedia contest formats"
						icon='knk-list'
					/>

					<Model model={stage} change={change}>
						<Checkbox
							name="media_image"
						>Allow photo and image file upload</Checkbox>

						<Checkbox
							name="media_video"
						>Allow video file upload</Checkbox>

						<Checkbox
							name="media_video_url"
						>Allow inserting YouTube videos</Checkbox>

						<Checkbox
							name="media_text"
						>Allow text-only content</Checkbox>

						{(text_permitted) && (
							<div class="not_in_top">
								<FieldLabel
									_extra_component={`${i18n("Limit")}: 5000`}
								>Minimum / maximum characters</FieldLabel>

								<Model
									errors={errorsMinMax}
									model={stage}
									change={changeMinMax}
									classname="gui_equal_columns gui_padded_children_low gui_not_in_bottom_big gui_flex_align_bottom"
								>
									<Input
										_class={{ first:true }}
										_type="number"
										name="media_text_min"
										value={stage?.media_text_min ?? 0}
										min={0}
										max={5000}
										hide_error_msg={true}
									/>

									<Input
										_class={{ first:true }}
										_type="number"
										name="media_text_max"
										value={stage?.media_text_max ?? 1500}
										min={0}
										max={5000}
										hide_error_msg={true}
									/>
								</Model>
							</div>
						)}

						<Checkbox
							name="media_pdf"
						>Allow PDF document upload</Checkbox>

						<Checkbox
							name="media_url"
						>Allow content based on external links</Checkbox>

						{/*<Checkbox
							name="media_audio"
						>Accept format audio</Checkbox>*/}

						{/* 
						//OUT Basic Display Instagram 4/12/24
						<Checkbox
							name="media_instagram"
						>Allow photos and videos from Instagram</Checkbox>
						*/}
					</Model>

					{/*(stage?.media_instagram ?? false) && (
						<Message>
							<p>
								{i18n("This option will be removed on December 4, 2024.")} <ActionLink
									_href={`${window.zendesk_base_url}31922056243220`}
									_target='_blank'
								>More information</ActionLink>
							</p>
						</Message>
					)}

					{(stage.media_instagram) && (
						<ItemAsRibbons
							_class={{ other:'gui_not_in_top' }}
							items={hashtags_ig}
							name='hashtag_instagram'
							onSubmit={(hashtag) => addHashtag(hashtag, 'instagram')}
							onChange={(k, v) => {
								setErrors({});
								setHashtagIG(v);
							}}
							value={hashtag_IG}
							model={stage}
							change={change}
							errors={errors}
							onDelete={(index) => removeHashtag(index, 'instagram')}
							placeholder="Type to add new #hashtag"
							tooltip={<Tooltip autotranslate={false}>{props.intl.formatMessage({ id:"Click the {more} button to add a hashtag before saving. Remember, hashtags must include the # symbol." }, { more:`'+'`})}</Tooltip>}
						>They must include one of these hashtags</ItemAsRibbons>
					)*/}

					<Model model={stage} change={change}>
						<Checkbox
							name="media_tiktok"
							autotranslate={false}
						>{`${i18n("Allow videos from TikTok")} (${i18n("only public videos")})`}</Checkbox>
					</Model>

					{(stage.media_tiktok) && (
						<ItemAsRibbons
							_class={{ other:'gui_not_in_top' }}
							items={hashtags_tt}
							name='hashtag_tiktok'
							onSubmit={(hashtag) => addHashtag(hashtag, 'tiktok')}
							model={stage}
							change={change}
							errors={errors}
							onChange={(k, v) => {
								setErrors({});
								setHashtagTT(v);
							}}
							value={hashtag_TT}
							onDelete={(index) => removeHashtag(index, 'tiktok')}
							placeholder="Type to add new #hashtag"
							tooltip={<Tooltip autotranslate={false}>{props.intl.formatMessage({ id:"Click the {more} button to add a hashtag before saving. Remember, hashtags must include the # symbol." }, { more:`'+'`})}</Tooltip>}
						>They must include one of these hashtags</ItemAsRibbons>
					)}
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Settings"
						icon='knk-settings'
					/>

					<Model model={stage} change={change}>
						<Range
							_class={{size:50}}
							min={1}
							max={media_max_elements}
							step={1}
							name="media_min_elements"
							value={media_min_elements}
						>Min. number of media contents</Range>

						<Range
							_class={{size:50}}
							min={media_min_elements}
							max={max_elements_to_upload}
							step={1}
							name="media_max_elements"
							value={media_max_elements}
						>Max. number of media contents</Range>

						{/*(uploaded_permitted) && (
							<Input
								name="media_placeholder"
								_class={{size:50}}
							>Uploader file label</Input>
						)*/}

						{(uploaded_permitted) && (
							<Select
								name="media_size"
								_class={{size:50}}
								values={sizes_file}
								default={stage.media_size}
							>Maximum file size</Select>
						)}

						{(!is_only_text) && (
							<Checkbox
								name="media_caption_hidden"
							>Hide file description</Checkbox>
						)}
					</Model>
					{(!is_only_text && (stage?.media_caption_hidden ?? false) === false) && (
						<>
							<Model model={stage} change={change}>
								<Checkbox
									name="media_caption_mandatory"
								>Required file description</Checkbox>
							</Model>
								
							{(isNotEmpty(stage,'media_caption_mandatory') || true) && (
								<div class="not_in_top">
									<FieldLabel
										_extra_component={`${i18n("Limit")}: 2500`}
									>Minimum / maximum characters</FieldLabel>

									<Model 
										errors={errorsMinMax}
										model={stage} 
										change={changeMinMax} 
										classname="gui_equal_columns gui_padded_children_low gui_not_in_bottom_big gui_flex_align_bottom"
									>
										<Input
											_class={{ first:true }}
											_type="number"
											name="media_caption_min"
											value={stage?.media_caption_min ?? 0}
											min={0}
											max={2500}
											hide_error_msg={true}
										/>

										<Input
											_class={{ first:true }}
											_type="number"
											name="media_caption_max"
											value={stage?.media_caption_max ?? 1500}
											min={0}
											max={2500}
											hide_error_msg={true}
										/>
									</Model>
								</div>
							)}
						</>
					)}
					<Model model={stage} change={change}>
						<RichPlaceHolder
							placeholder={default_description_text}
							name="main_description"
							has_emojis={true}
						>Introduction message</RichPlaceHolder>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="landing" menus={props.promoModel.Custom__Menus}>
					<div class={(window.server_type != '2') ? 'gui_withoutEvents' : ''} style={{width:'100%'}}>
						<MediaParticipation
							stage={{Stage:stage}}
							promotion={props.promotion}
							initial_step={1}
							is_builder={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const MediaMain = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_enabled = stage?.media_has_step_0 ?? true;

	const default_description_text = `<h1>${i18n("Submit your entry")}</h1><p>${i18n("Complete all the fields, and remember that you can enter emojis to make your entry stand out and shine.")}</p>`;

	const is_media_autocontent = (stage.promotion_type_id == stage_promotion_types.MEDIA_AUTOCONTENT.id) ? true : false;

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	//const default_extra_fields_text = `<p>${i18n("The following information will not be public")}:</p>`;
	const hide_nomination_name = stage?.hide_media_name_label ?? false; // Camp antic
	const show_nomination_name = stage?.media_has_name_label ?? !hide_nomination_name; //Posar '?? true' quan considerem que el camp vell no s'utilitza/afecta mes
	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={props.nav_active}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Model model={stage} change={change}>
					<Checkbox
						name="media_has_step_0"
						_class={{ first:true }}
						value={is_enabled}
						label_props={{
							tooltip:<Tooltip>{`${i18n("Enable this option in contests where users submit nominations to be voted on that are not on their behalf. For example, contests for musical bands, contests for the best project, etc.")}<br /><br />${i18n("Do not enable the option for simple contests involving photos, videos, or stories, where the user participates as the content's author to be voted on.")}`}</Tooltip>
						}}
					>Request nomination information</Checkbox>
				</Model> 

				{(is_enabled) && (
					<Model model={stage} change={change}>
						<RichPlaceHolder
							placeholder={default_description_text}
							name="media_description_step0"
							has_emojis={true}
						>Introduction message</RichPlaceHolder>
					</Model>
				)}

				{(is_enabled) && (
					<Model model={stage} change={change}>
						{/*<Checkbox
							name="hide_media_name_label"
							value={stage?.hide_media_name_label ?? true}
							label_props={{
								tooltip:<Tooltip>If you do not request the name, the default name for the entry will be that of the participating user.</Tooltip>
							}}
						>Hide name</Checkbox>*/}

						<Checkbox
							name="media_has_name_label"
							value={show_nomination_name}
							label_props={{
								tooltip:<Tooltip>Allows users to enter a name for their nomination, such as a pseudonym, nickname, or alias. If this option is enabled, the provided name will replace the First and Last Name from the registration form and will be displayed publicly in the contest gallery.</Tooltip>
							}}
						>Request Nomination name</Checkbox>

						{(stage?.media_has_name_label ?? true) && (
							<Input
								name="media_name_label"
								placeholder={i18n("***Name")}
							>{props.intl.formatMessage({ id:"Label for {field} field" }, { field:i18n("***Name") })}</Input>
						)}

						<Checkbox
							name="media_has_short_description"
							value={stage?.media_has_short_description ?? true}
						>Request a short description</Checkbox>

						<Checkbox
							name="media_has_avatar"
							value={stage?.media_has_avatar ?? true}
						>Request a profile picture</Checkbox>

						<Checkbox
							name="media_has_country"
							value={stage?.media_has_country ?? true}
						>Request the entry country</Checkbox>

						<Checkbox
							name="media_has_links"
							value={stage?.media_has_links ?? true}
						>Request links and social networks</Checkbox>
					</Model>
				)}

				{(is_enabled && !is_media_autocontent) && (
					<article class="gui_not_in_top_huge">
						<DynamicForm
							form={stage?.media_form ?? []}
							onChange={(v) => change('media_form', v)}
							section_title="Extra fields"
							subtitle="You can request extra fields to complete the user participation. By default, these fields will be private only available to admins. However, you can choose to make them public to display extra information in the contest gallery."
							section_icon="knk-list"
							add_field_label="Add field"
							locales={avail_locales}
							has_translation={true}
							enable_public_value={['field', 'dropdown']}
						/>

						{/*((stage?.media_form ?? []).length > 0) && (
							<Model model={stage} change={change}>
								<RichPlaceHolder
									placeholder={default_extra_fields_text}
									name="media_extra_fields_message"
									has_emojis={true}
								>Introduction message</RichPlaceHolder>
							</Model>
						)*/}
					</article>
				)}
			</BuilderSidebar>

			{(is_enabled) ? (
				<BuilderCanvas fit_to_window="content">
					<Layout promo={props.promotion} is_builder={true} page="landing" menus={props.promoModel.Custom__Menus}>
						<div class={(window.server_type != '2') ? 'gui_withoutEvents' : ''} style={{width:'100%'}}>
							<MediaParticipation
								stage={{Stage:stage}}
								promotion={props.promotion}
								initial_step={0}
								is_builder={true}
							/>
						</div>
					</Layout>
				</BuilderCanvas>
			) : (
				<BuilderCanvas fit_to_window="content" bordered={false} not_awesome={true}>
					<div>
						<p>{i18n("The screen where you request information about the nomination to be voted on is disabled.")}</p>

						<p>
							<FormattedMessage
								id="Users, when attempting to enter, will directly see the {link} of their entry."
								values={{
									link:<ActionLink _href={`#stages/${stage.id}/media/settings`}>screen for uploading the multimedia content</ActionLink>
								}}
							/>
						</p>
					</div>
				</BuilderCanvas>
			)}
		</>
	)
};

export const Step0 = ({ state, change, changeUser, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_builder = props?.is_builder ?? false;
	const is_frontend = props?.is_frontend ?? false;

	const stage = props?.stage?.Stage ?? false;
	const errors = props?.errors ?? {};
	const errors_extra = errors?.extra ?? {};

	const changeExtra = (k, v, id) => {
		let _extra = (isNotEmpty(props.User, 'extra')) ? props.User.extra : {} ;
		if(id != null) {
			if(!_extra[id]) _extra[id] = {};
			_extra[id][k] = v;
		} else {
			_extra[k] = v;
		}
		changeUser('extra', _extra);
	};

	const changeAvatar = (k, v) => {
		if(!v){
			changeUser('avatar', null);
			changeUser('hashtag_avatar', null);
			return;
		}else{
			if(k == 'full_avatar') changeUser('avatar', v)
			if(k != 'avatar') return;
			changeUser('hashtag_avatar', v)
		}
	};

	const default_input_size = (!is_frontend) ? 70 : null;

	const hide_nomination_name = stage?.hide_media_name_label ?? false; // Camp antic
	const enable_name = stage?.media_has_name_label ?? !hide_nomination_name; //Posar '?? true' quan considerem que el camp vell no s'utilitza/afecta mes
	const enable_short_description = stage?.media_has_short_description ?? true;
	const enable_avatar = stage?.media_has_avatar ?? true;
	const enable_country = stage?.media_has_country ?? true;
	const enable_links = stage?.media_has_links ?? true;

	const mandatory_label = (is_frontend) ? `<span class="is_mandatory_icon"><span /></span>` : '';
	const optional_label = (is_frontend) ? ` (${i18n("Optional", 'lowercase')})` : '';
	return (
		<>	
			{(enable_name || is_builder) && (
				<Model model={props.User} change={changeUser} errors={errors}>
					<Input
						name="name"
						max_length={50}
						disabled={(!is_builder && !is_frontend) ? true : false}
						autotranslate={false}
						_class={{ first:true, size:default_input_size, other:(!is_frontend) ? 'gui_not_in_bottom' : null }}
					>{`${(isNotEmpty(stage, 'media_name_label')) ? stage.media_name_label : i18n("***Name")}${mandatory_label}`}</Input>
				</Model>
			)}

			{(enable_short_description || is_builder) && (
				<Model model={props.User.extra} change={changeExtra} errors={errors_extra}>
					<TextArea
						name="short_description"
						disabled={(!is_builder && !is_frontend) ? true : false}
						_class={{ size:default_input_size }}
						autotranslate={false}
					>{`${i18n("Description")}${(enable_short_description) ? mandatory_label : optional_label}`}</TextArea>
				</Model>
			)}

			{(enable_avatar || is_builder) && (
				<UploadAvatar
					user={{
						promotion_id:props.promotion.id,
						avatar:props.User?.hashtag_avatar ?? null,
						full_avatar:props.User?.avatar ?? null,
					}}
					type='back'
					is_builder={is_builder}
					single_stage={true}
					use_empty_by_default={false}
					width='100%'
					//error={errors?.hashtag_avatar ?? false}
					//height={90}
					input_classes={{ size:default_input_size, is_first:false }}
					small_input={true}
					is_first={false}
					autotranslate={false}
					upload_message="Upload image"
					change={changeAvatar}
					display_network_icon={true}
				>{`${i18n("Profile picture")}${(enable_avatar) ? mandatory_label : optional_label}`}</UploadAvatar>
			)}

			{(enable_country || is_builder) && (
				<CountryField2
					country={props.User?.country ?? ''}
					onChange={( prefix, country ) => changeUser('country', country.k)}
					errors={errors}
					name="country"
					_class={{ size:default_input_size }}
					autotranslate={false}
					only_available={false}
					position='top'
					display_error_at_bottom={true}
				>{`${i18n("Country")}${(enable_country) ? mandatory_label : optional_label}`}</CountryField2>
			)}

			{(props.children != undefined) && (
				<div class="not_in_top">
					{props.children}
				</div>
			)}

			{(enable_links || is_builder) && (
				<>
					<Model model={props.User.extra} change={changeExtra} errors={errors_extra}>
						<UrlField
							name='external_link'
							_class={{ size:default_input_size }}
							autotranslate={false}
							force_model_errors={errors_extra?.external_link ?? false}
						>{`${i18n("External link")}${optional_label}`}</UrlField>

						{(isNotEmpty(props.User.extra, 'external_link')) && (
							<Input
								name='external_link_label'
								_class={{ size:default_input_size }}
								autotranslate={false}
							>{`${i18n("Link label")}${optional_label}`}</Input>
						)}
					</Model>

					<article class="gui_not_in_top_huge">
						{(is_frontend) ? (
							<FieldLabel
								_class={{ other:'networks_header' }}
								autotranslate={false}
							>{i18n("Add your networks to provide more information about you")}{optional_label}</FieldLabel>
						) : (
							<BuilderSubSectionTitle
								title="Networks"
								icon='knk-networks'
							/>
						)}

						<Model
							model={props.User.extra?.nets ?? {}}
							change={(k, v) => changeExtra(k, v, 'nets')}
							classname='social_network_links'
						>
							<UrlField
								name='facebook'
								_class={{ size:70 }}
								_field_class='facebook_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-facebook',
									},
								}}
								placeholder='https://www.facebook.com/...'
								ensure_domain='facebook.com'
							>Facebook</UrlField>

							<UrlField
								name='twitter'
								_class={{ size:70 }}
								_field_class='twitter_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-twitter',
									},
								}}
								placeholder='https://www.twitter.com/...'
								ensure_domain={['twitter.com', 'x.com']}
							>Twitter</UrlField>

							<UrlField
								name='instagram'
								_class={{ size:70 }}
								_field_class='instagram_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-instagram',
									},
								}}
								placeholder='https://www.instagram.com/...'
								ensure_domain='instagram.com'
							>Instagram</UrlField>

							<UrlField
								name='linkedin'
								_class={{ size:70 }}
								_field_class='linkedin_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-linkedin',
									},
								}}
								placeholder='https://www.linkedin.com/...'
								ensure_domain='linkedin.com'
							>LinkedIn</UrlField>

							<UrlField
								name='youtube'
								_class={{ size:70 }}
								_field_class='youtube_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-youtube',
									},
								}}
								placeholder='https://www.youtube.com/...'
								ensure_domain='youtube.com'
							>YouTube</UrlField>

							<UrlField
								name='twitch'
								_class={{ size:70 }}
								_field_class='twitch_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-twitch',
									},
								}}
								placeholder='https://www.twitch.tv/...'
								ensure_domain='twitch.tv'
							>Twitch</UrlField>

							<UrlField
								name='tiktok'
								_class={{ size:70 }}
								_field_class='tiktok_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-tiktok',
									},
								}}
								skip_chars = {['@']}
								placeholder='https://www.tiktok.com/@username'
								ensure_domain='tiktok.com'
							>TikTok</UrlField>
							
							<UrlField
								name='threads'
								_class={{ size:70 }}
								_field_class='threads_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-threads',
									},
								}}
								skip_chars = {['@']}
								placeholder='https://www.threads.net/@username'
								ensure_domain='threads.net'
							>Threads</UrlField>
							
							<UrlField
								name='pinterest'
								_class={{ size:70 }}
								_field_class='pinterest_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-pinterest',
									},
								}}
								placeholder='https://www.pinterest.com/...'
								ensure_domain='pinterest.com'
							>Pinterest</UrlField>
							
							<UrlField
								name='spotify'
								_class={{ size:70 }}
								_field_class='spotify_url'
								label_props={{
									autotranslate:false,
									_class:{
										icon:'ep-spotify',
									},
								}}
								placeholder='https://open.spotify.com/...'
								ensure_domain='spotify.com'
							>Spotify</UrlField>
						</Model>
					</article>
				</>
			)}

			<ButtonCollection in_floating={true}>
				{props.layer_actions}
			</ButtonCollection>
		</>
	)
};

export const MediaCandidates = ({ base_route, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	useCSS('comp/owner_multigallery.css');

	const owner = useOwner();

	const history = useHistory();

	const [ state, dispatch ] = useReducer(( state, action) => {
		switch(action.type) {
			case 'CREATE_CANDIDATE':
				return {
					...state,
					step:0,
					candidate:{
						User:{
							net_type:12,
						},
					},
				};

			case 'CLOSE_CANDIDATE':
				return {
					...state,
					candidate:false,
				};

			case 'EDIT_CANDIDATE':
				return {
					...state,
					step:0,
					candidate:action.candidate,
				};

			case 'TOGGLE_INTERNAL_COMMENT_LAYER':
				return {
					...state,
					display_internal_comment:!state.display_internal_comment,
					user_for_comment:action.user_for_comment,
				};

			case 'TRANSLATE_CANDIDATE':
				return {
					...state,
					translate:action.candidate,
				};

			case 'TRANSLATE':
				if(!isNotEmpty(state.translate.User, 'extra')) state.translate.User.extra = {};
				if(state.translate.User.extra.loc == undefined) state.translate.User.extra.loc = {};
				if(state.translate.User.extra.loc[action.loc] == undefined) state.translate.User.extra.loc[action.loc] = {};

				if(action.v == '' && state.translate.User.extra.loc[action.loc][action.k] != undefined) {
					delete state.translate.User.extra.loc[action.loc][action.k];
				} else {
					state.translate.User.extra.loc[action.loc][action.k] = action.v;
				}
				return {
					...state
				}

			case 'TRANSLATE_MEDIA':
				if(action.k == 'full_name') return state;
				return {
					...state,
					translate:{
						User:{
							...state.translate.User,
							quiz_entry: {
								...state.translate.User.quiz_entry,
								media:state.translate.User.quiz_entry.media.map((field) => {
									if(field.id != action.id) return field;
									if(field.loc == undefined ) field.loc = {};
									if(field.loc[action.loc] == undefined) field.loc[action.loc] = {};
									if(action.v == null) action.v = "";
									if(action.v == '' && field.loc[action.loc][action.k] != undefined) {
										delete field.loc[action.loc][action.k];
									} else if(action?.field_type == 'youtube') {
										field.loc[action.loc].url = action.v.url;
										field.loc[action.loc].youtube_id = action.v.youtube_id;
										field.loc[action.loc].caption = action.v.caption;
										field.loc[action.loc] = {...field.loc[action.loc]}
									} else {
										field.loc[action.loc][action.k] = action.v;
									}
									return {...field};
								})
							}
						}
					}
				}

			case 'TRANSLATE_NETS':
				const nets = state.translate.User.extra.nets;

				if(nets.loc == undefined) nets.loc = {};
				if(nets.loc[action.loc] == undefined) nets.loc[action.loc] = {};
				if(action.v == null) action.v = "";
				if(action.v == '' && nets.loc[action.loc][action.k] != undefined) {
					delete nets.loc[action.loc][action.k];
				} else {
					nets.loc[action.loc][action.k] = action.v;
				}

				return {
					...state,
					translate:{
						User:{
							...state.translate.User,
							extra:{
								...state.translate.User.extra,
								nets:{...nets},
							}
						}
					}
				}

			case 'OPEN_ENTRY':
				return {
					...state,
					//candidate:state.candidates.filter((c) => (c.User.id != action.candidate_id)),
					current_participation:action.participation,
					current_media:0,
				};
	
			case 'CLOSE_ENTRY':
				return {
					...state,
					current_participation:false,
					current_media:0,
				};
	
			case 'DELETE_CANDIDATE':
				return {
					...state,
					candidates:state.candidates.filter((c) => (c.User.id != action.candidate_id)),
					candidate:false,
				};

			case 'CHANGE':
				return {
					...state,
					[action.k]:action.v,
				};

			case 'CHANGE_EXTRA':
				return {
					...state,
					extra:{
						...state.extra,
						[action.k]:action.v,
					},
				};

			case 'SET_CANDIDATES':
				return {
					...state,
					candidates:action.candidates,
					step:0,
				};

			case 'ADD_CANDIDATE':
				return {
					...state,
					candidates:[
						...state.candidates,
						action.candidate,
					],
					candidate:false,
				};

			case 'REPLACE_CANDIDATE':
				return {
					...state,
					candidates:state.candidates.map(c=>{
						if(c.User.id != action.candidate.User.id) return c;
						return action.candidate;
					}),
					candidate:false,
				};

			default:
				return state;
		}
	}, {
		display_internal_comment:false,
		export_modal:false,
		export_zip:false,
		full_view:false,
		export_multimedia:false,
		display_full_entry:false,
		candidate:false,
		translate:false,
		current_participation:false,
		current_media:0,
		search:"",
		User:{},
		extra:{},
		user_for_comment:{},
		candidates:props?.candidates ?? [],
	});

	const public_fields = (props.stage?.media_form ?? []).filter(f=>f.public == 'true');

	const getUsers = async (start = false, limit = 100) => {
		const users = await $.getJSON(getRemotedbUrl(`/users/media/get/${props.stage.promotion_id}/${props.stage.id}`), { start, limit });

		users.data = users.data.map(user=>{
			user.User.public_extra_fields = {
				fields:public_fields,
				values:user.User?.quiz_entry?.form
			}
			return user
		})
		return users;
	};

	const [ candidates, dispatchCandidate ] = useFilterDataQuery(`candidates`, getUsers, {
		indexer:(indexed, element) => {},
		limit:100,
		pagination:1000,
		fetch_all_disabled:false,
	});

	const [ data, pagination, goToPage ] = usePaginator(candidates.filtered, 100);

	const searchEntries = (k, v) => {
		const filter = {
			key:'search',
			name:i18n("Name"),
			tier1:['User.name','User.extra.short_description'],
			tier2:{ ...operators.textOperators },
			tier3:'input',
			type:'string',
		};
	
		dispatchCandidate({
			type:'SETFILTER',
			filter:{
				key:'search',
				value:v,
				operator:'like_all',
				filter,
			},
			unique:true,
		});
	
		dispatch({ type:'CHANGE', k:'search', v });
	};
	
	const saveChanges = (e,cb) => {
		$.post(getRemotedbUrl(`/stages/save_candidate/${props.promotion.id}/${props.stage.id}`), {
			user:{
				User:{
					...state.translate.User,
				},
			},
		}, (result) => {
			if(cb != undefined) cb(result);
			dispatch({ type:'REPLACE_CANDIDATE', candidate:result.user });
		}, 'JSON');
	};

	const closeWholeUserLayer = () => {
		dispatch({ type:'CHANGE', k:'display_full_entry', v:false });
	};

	const is_agc = (props.stage.promotion_type_id == stage_promotion_types.MEDIA_AUTOCONTENT.id) ? true : false;	// Admin Generated Content

	const root_basename = `stages/categories`;
	const basename = `${root_basename}/${props.stage.id}`;
	const nav_sections = [
		{ label:"Go back", link:`${root_basename}`, icon:'knk-go-prev' },
		{ label:"Candidates list", link:`${basename}`, icon:'knk-entries' },
	];
	if(is_agc) {
		nav_sections.push({ label:"Settings", link:`${basename}/settings`, icon:'knk-settings' });
	}

	const userAction = (type, ids, data, cb) => {
		bulkAction(type, cb, ids, data);
	};

	const bulkAction = (type, cb, ids, data) => {
		let bulk = null;

		if(ids == undefined) ids = state.filtered.map((u) => u.User.id);

		const info = {
			ids:JSON.stringify(ids)
		};

		switch(type) {
			case 'reject':
				bulk = (u) => {
					u.User.public_entry = 4;
					return u;
				}
				break;
			case 'accept':
				bulk = (u) => {
					u.User.public_entry = 1;
					return u;
				}
				break;
			case 'pending':
				bulk = (u) => {
					u.User.public_entry = 0;
					return u;
				}
				break;
			case 'extra_comment':
				bulk = (u) => {
					u.User.extra.comment = data?.comment ?? '';
					return u;
				}
				break;
		}

		if(bulk) {
			dispatchCandidate({
				type:'APPLYBULK',
				bulk,
				ids,
			});
		}
		if(type == 'extra_comment')return; 
		$.post(getRemotedbUrl(`/users/apply_bulk/${type}/${props.promotion.id}`), info, (result) => {
			if(cb != undefined) cb();
		});
	};

	const openWholeUserLayer = (login_id) => {
		$.get(getRemotedbUrl(`/users/get_full_view_data/${props.promotion.id}/${login_id}`), (res) => {
			if(res.status == '1') {
				dispatch({type:'CHANGE',k:'full_view',v:res.data});
			}
		}, 'JSON');
	};

	const main_color = props.promotion?.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)';
	const colors = (props.stage.media_colors == undefined) ? main_color : props.stage.media_colors;

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	const is_wl = (props.promotion.promo_type == '2') ? true : false;
	const _stages = props?.stages ?? [];
	const is_mirrored = _stages.find((s) => { return s.Stage.voting_mirror == props.stage.id }) != undefined ? true : false;
	const enable_delete_category = (is_wl && state.candidates.length == 0 && !is_mirrored) ? true : false;
	const original_stage = _stages.find((s) => { return s.Stage.id == props.stage.id });
	const is_stage_photofun = (stage_categories.ID_PHOTO_FUN.id == original_stage.Stage.type) ? true : false;

	const entry_statuses = [
		{ k:'0', v:i18n("pending", 'ucfirst'), color:'orange' },
		{ k:'1', v:i18n("Active"), color:'green' },
		{ k:'4', v:i18n("Rejected"), color:'red' },
		//{ k:'6', v:i18n("In process"), color:'gray' },
	];

	const changeUserStatus = (user_id, new_status) => {
		let action = false;
		switch(new_status){
			case '1':action='accept'; break;
			case '4':action='reject'; break;
			case '0':action='pending'; break;
		}
		userAction(action,[user_id],)
	};

	const tz = props?.promotion?.timezone ?? defaults.timezone;
	return (
		<>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						{(!is_agc && !is_stage_photofun) && (
							<BuilderMainWithoutSidebar pm={props.promoModel} has_tabs={true} not_padded={true}>
								<NavigationTabsBuilder
									active={`/${basename}/settings`}
									sections={nav_sections ?? []}
								>
									{props.stage_indicator}

									<ButtonCollection>
										<CreateButton
											onClick={() => dispatch({ type:'CREATE_CANDIDATE' })}
										>Create new candidate</CreateButton>
										
										{(enable_delete_category) && (
											<DeleteButton
												_position="bottom"
												onClick={() => props.deleteStage(props.stage.id)}
											>Delete category</DeleteButton>
										)}
									</ButtonCollection>
								</NavigationTabsBuilder>

								<div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', textAlign:'center' }}>
									<p>
										<FormattedMessage
											id="In order to customize this type of stage, please go to {link}."
											values={{
												link:<strong><br />{i18n("Participation stages")} &gt; <ActionLink
													_href={`#stages/${props.stage.id}/media/main`}
												>Submit Multimedia Content</ActionLink></strong>,
											}}
										/>
									</p>
								</div>
							</BuilderMainWithoutSidebar>
						)}

						{(is_agc) && (
							<BuilderMain pm={props.promoModel}>
								<BuilderSidebar onSave={props.saveStage}>
									<NavigationTabsBuilder
										active={`/${basename}/settings`}
										sections={nav_sections ?? []}
									>
										{props.stage_indicator}

										<ButtonCollection>
											<CreateButton
												onClick={() => dispatch({ type:'CREATE_CANDIDATE' })}
											>Create new candidate</CreateButton>

											{(enable_delete_category) && (
												<DeleteButton
													_position="bottom"
													onClick={() => props.deleteStage(props.stage.id)}
												>Delete category</DeleteButton>
											)}
										</ButtonCollection>
									</NavigationTabsBuilder>

									<article>
										<Model model={props.stage} change={props.change}>
											<Input
												name="display_title"
												_class={{ first:true, size:50 }}
											>Category name</Input>
										</Model>
									</article>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Cover"
											icon='knk-scene'
										/>

										<Model model={props.stage} change={props.change}>
											<Checkbox
												name="media_title"
											>Enable cover</Checkbox>

											<Checkbox
												name="media_description_checkbox"
											>Enable cover description</Checkbox>
										</Model>
									</article>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Colors"
											icon='knk-artist-mode'
										/>

										<Model model={props.stage} change={props.change}>
											<ColorPairsSelector
												name="media_colors"
												label_props={{
													tooltip:<Tooltip>Allows users to pick a color for their entries. This color will appear as background color for text-only content and for the cover if it is enabled. You can add up to 5 colors for them to choose from. If you don't add any, the default color of the promotion will be used.</Tooltip>,
												}}
												skip_textcolor={true}
												min_colors={1}
												max_colors={5}
												defs={{background:main_color}}
												value={colors}
											>Available background colors</ColorPairsSelector>
										</Model>
									</article>
								</BuilderSidebar>

								<BuilderCanvas not_awesome={true} bordered={false}>
									<ul class="gui_padded_children_v">
										<li><strong>{i18n("Cover")}:</strong><p>{i18n("You can enable a cover to introduce a candidate. Instead of displaying a picture or video of the candidate, you would prominently feature the candidate's name or project.")}</p></li>

										<li><strong>{i18n("Colors")}:</strong><p>{i18n("Define the background colors available in case you enable the cover or add media for a candidate based on text.")}</p></li>
									</ul>
								</BuilderCanvas>
							</BuilderMain>
						)}
					</Route>

					<Route exact={false} path="/">
						<BuilderMainWithoutSidebar pm={props.promoModel} not_padded={true} has_tabs={true}>
							<NavigationTabsBuilder
								active={`/${basename}`}
								sections={nav_sections ?? []}
							>
								{props.stage_indicator}

								<ButtonCollection>
									<SecondaryButton
										icon='knk-export-users'
										onClick={() => dispatch({ type:'CHANGE',k:'export_modal', v:true })}
									>Export entries</SecondaryButton>

									{/*<SecondaryButton
										icon='knk-download'
										onClick={() => dispatch({ type:'CHANGE',k:'export_multimedia', v:true })}
											>Export files for bulk download</SecondaryButton>*/}

									<SecondaryButton
										icon='knk-download'
										onClick={() => dispatch({ type:'CHANGE',k:'export_zip', v:true })}
									>Download Multimedia Content</SecondaryButton>

									{(!is_stage_photofun) && (
										<CreateButton
											onClick={() => dispatch({ type:'CREATE_CANDIDATE' })}
										>Create new candidate</CreateButton>
									)}

									{(enable_delete_category) && (
										<DeleteButton
											_position="bottom"
											onClick={() => {
												props.deleteStage(props.stage.id);
												history.push(`/${root_basename}`);
											}}
										>Delete category</DeleteButton>
									)}
								</ButtonCollection>
							</NavigationTabsBuilder>

							<div class="builder_full_canvas_container with_tabs not_overflowable">
								<div class="_header">
									<SectionTitle autotranslate={false}>{i18n("Category")}: {(isNotEmpty(props.stage, 'display_title')) ? props.stage.display_title : `#${props.stage.id}`}</SectionTitle>

									<SectionSubtitle autotranslate={false}>{(state.candidates.length != 1) ? props.intl.formatMessage({ id:"{num} candidates" }, { num:candidates.filtered.length }) : i18n("1 candidate")}</SectionSubtitle>

									<SearchField
										value={state.search}
										change={searchEntries}
										name="search"
									/>
								</div>

								<div class='gui_overflowed_auto' style={{ height:'calc(100dvh - 278px)' }}>
									<ul class="gui_white_bg_total gui_multimedia_gallery has_column_of_3 gui_main_text_color_on_bg">
										{data.map((user) => {
											if(user.User == undefined) return null;

											const composite_name = `${user.User.first_name} ${user.User.last_name}`;

											let user_status = user.User.public_entry;
											if(user_status == '2') user_status = '1';
											if(user_status == '3') user_status = '1';
											
											return (
												<SingleGallery
													item_id={`gallery_${user.User.id}`}
													key={user.User.id}
													promotion={props.promotion}
													stage={props.stage}
													user={{ Participation:user.User }}
													has_voting_sidebar={false}
													is_votable={false}
													is_builder={true}
													fake_frontend={true}
													selected_entry={(state.current_participation !== false && state.current_participation.id == user.User.id) ? user.User.id : null}
													onEdit={() => dispatch({ type:'EDIT_CANDIDATE', candidate:user })}
													openDetailLayer={() => dispatch({ type:'OPEN_ENTRY', participation:user.User })}
													closeEntryAsLayer={() => dispatch({ type:'CLOSE_ENTRY' })}
													is_editable={true}
													open_full_view={()=> openWholeUserLayer(user.User.login_id)}
													extra_footer_button={<>
														{(avail_locales.length > 0) ?
															<SquaredButton
																icon='knk-translate'
																onClick={(e) => {
																	//e.stopPropagation();
																	dispatch({ type:'TRANSLATE_CANDIDATE', candidate:user });
																}}
																tooltip={<Tooltip position='top' with_icon={false}>Translate this entry</Tooltip>}
																aria-label="Translate this entry"
															/>
														: null}

														<SelectRibbon
															items={entry_statuses}
															value={user_status}
															item_id={user.User.id}
															onSelect={changeUserStatus}
														/>
													</>}
													additional_footer={<div class="internal_comment">
														<p>
															{(user.User.net_type == 12) && (
																<FormattedMessage
																	id="By {name}, {date} h"
																	values={{
																		name:<strong>{i18n("Admin")}</strong>,
																		date:EPGetTimezone(user.User.created,tz, 'DD MMM YYYY, HH:mm'),
																	}}
																/>
															)}

															{(user.User.net_type != 12 && user?.Stage0?.name != composite_name) && (
																<FormattedMessage
																	id="By {name}, {date} h"
																	values={{
																		name:<strong>{`${user.Stage0?.first_name ?? user.Stage0.name} ${user.Stage0?.last_name ?? ''}`}</strong>,
																		date:EPGetTimezone(user.User.created,tz, 'DD MMM, HH:mm'),
																	}}
																/>
															)}
														</p>

														<div class={`comm${(isNotEmpty(user.User.extra, 'comment')) ? ' has_comment' : ''}`} onClick={(e) => { e.stopPropagation(); dispatch({ type:'TOGGLE_INTERNAL_COMMENT_LAYER', user_for_comment:user.User }); }}>
															{(isNotEmpty(user.User.extra, 'comment')) && (
																<p dangerouslySetInnerHTML={{ __html:user.User.extra.comment }} />
															)}
															
															<ActionLink>{(isNotEmpty(user.User.extra, 'comment')) ? "Edit" : "Add internal note"}</ActionLink>
														</div>
													</div>}
													intl={props.intl}
												/>
											)
										})}
									</ul>
								</div>

								<Paginator
									{...pagination}
									goToPage={goToPage}
									min_pages_to_display={2}
									intl={props.intl}
								/>

								{(state.display_internal_comment !== false) && (
									<InnerComment
										participation={state.user_for_comment}
										dispatch={dispatch}
										onSave ={(comment) => {
											userAction('extra_comment', [ state.user_for_comment.id ], { comment });
										}}
										backButton={() => {
											dispatch({ type:'TOGGLE_INTERNAL_COMMENT_LAYER', candidate:false, user_for_comment:false });
										}}
									/>
								)}
							</div>
						</BuilderMainWithoutSidebar>
					</Route>
				</Switch>
			</Router>

			{(state.translate != false) && (
				<FloatingLayer backButton={() => dispatch({ type:'TRANSLATE_CANDIDATE', candidate:false })}>
					<div>
						<TranslationField
							locales={avail_locales}
							fields={[{
								...state.translate.User,
								type:'stage_0',
								short_description:state.translate.User?.extra?.short_description ?? "",
								external_link:state.translate.User?.extra?.external_link ?? "",
								external_link_label:state.translate.User?.extra?.external_link_label ?? "",
								loc:state.translate.User?.extra?.loc ?? {},
							}]}
							main_section_name={i18n("Main information")}
							main_section_with_index={false}
							dispatch={dispatch}
							field_types={[{
								type:'stage_0',
								translate:[{
									key:'name',
									type:'input',
									label:"***Name",
								}, {
									key:'short_description',
									type:'textarea',
									label:"Short description",
								}, {
									key:'external_link',
									type:'url',
									label:"External link URL",
								}, {
									key:'external_link_label',
									type:'input',
									label:"External link label",
								}],
							}]}
							entities={[{
								name:'networks',
								section_name:i18n("Networks"),
								section_with_index:false,
								fields:(state.translate.User?.extra?.nets != undefined ? [
									{...state.translate.User.extra.nets,type:'networks'}
								] : []),
								dispatch:(action) => {
									if(action.type == 'TRANSLATE') action.type = 'TRANSLATE_NETS';
									dispatch(action);
								},
								field_types:[{
									type:'networks',
									translate:[{
										key:'facebook',
										type:'url',
										label:"Facebook profile",
										ensure_domain:'facebook.com',
									},{
										key:'twitter',
										type:'url',
										label:"Twitter profile",
										ensure_domain:['twitter.com', 'x.com'],
									}, {
										key:'instagram',
										type:'url',
										label:"Instagram profile",
										ensure_domain:'instagram.com',
									}, {
										key:'linkedin',
										type:'url',
										label:"LinkedIn profile",
										ensure_domain:'linkedin.com',
									}, {
										key:'youtube',
										type:'url',
										label:"YouTube profile",
										ensure_domain:'youtube.com',
									}, {
										key:'twitch',
										type:'url',
										label:"Twitch profile",
										ensure_domain:'twitch.tv',
									}, {
										key:'tiktok',
										type:'url',
										label:"TikTok profile",
										ensure_domain:'tiktok.com',
									}, {
										key:'pinterest',
										type:'url',
										label:"Pinterest profile",
										ensure_domain:'pinterest.com',
									}, {
										key:'spotify',
										type:'url',
										label:"Spotify profile",
										ensure_domain:'spotify.com',
									}
								]}],
							}, {
								name:'media',
								fields:state.translate.User.quiz_entry.media,
								dispatch:(action) => {
									if(action.type == 'TRANSLATE') action.type = 'TRANSLATE_MEDIA';
									dispatch(action);
								},
								field_types:[{
									type:'image',
									translate:[{
										key:'name',
										type:'image',
										uploader:{
											auto_type:'promotion',
											auto_url:`/stages/upload/${props.stage.id}`,
											small_input:false,
											delete_position:'bottom',
										}
									}, {
										key:'caption',
										type:'textarea',
										label:"Caption",
									}],
								}, {
									type:'youtube',
									translate:[{
										key:'url',
										type:'youtube',
										label:"YouTube video URL",
									}, {
										key:'caption',
										type:'textarea',
										label:"Caption",
									}],
								}, {
									type:'video',
									translate:[{
										key:'caption',
										type:'textarea',
										label:"Caption",
									}],
								}, {
									type:'text',
									translate:[{
										key:'text',
										type:'textarea',
									}],
								}, {
									type:'url',
									translate:[{
										key:'url',
										type:'input',
										label:"URL",
									}, {
										key:'title',
										type:'input',
										label:"URL label",
									}, {
										key:'description',
										type:'textarea',
										label:"Description",
									}],
								}, {
									type:'pdf',
									translate:[{
										key:'name',
										type:'pdf',
										label:"PDF",
										uploader:{
											auto_type:'promotion',
											auto_url:`/stages/upload/${props.stage.id}`,
											small_input:false,
											delete_position:'bottom',
										}
									}],
								}],
							}]}
							intl={props.intl}
						/>

						<ButtonCollection in_floating={true}>
							<SaveButton onClick={saveChanges}>Save</SaveButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}

			{(state.candidate != false) && (
				<CandidateManager 
					promotion={props.promotion}
					stage={props.stage}
					user={state.candidate}
					allow_delete={ state.candidate.User.id != undefined}
					is_edit_builder={ state.candidate.User.id != undefined}
					close={() => dispatch({ type:'CLOSE_CANDIDATE' })}
					onSave={(candidate) => {
						const id = state.candidate.User?.id ?? false;
						if(id != false) {
							dispatchCandidate({
								type:'APPLYBULK',
								bulk:(u)=>(candidate),
								ids:[id],
							});
						} else {
							dispatchCandidate({
								type:'PREPEND_DATA',
								data:[candidate]
							});
						}
						dispatch({type:"CLOSE_CANDIDATE"});
					}}
					onDelete={(candidate_id) => {
						dispatchCandidate({ type:'REMOVE_USER', uid:candidate_id })
						dispatch({ type:'CLOSE_CANDIDATE' });
					}}
				/>
			)}

			{(state.display_full_entry !== false) && (
				<FloatingLayer backButton={() => closeWholeUserLayer()}>
					<div class="without_actions">
						<SingleRegisteredEntry
							{...state.display_full_entry}
							actions={false}
							userAction={userAction}
						/>
					</div>
				</FloatingLayer>
			)}

			{(state.export_modal !== false) && (
				<ExportModal
					stage_id={props.stage.id}
					stages={props.stages}
					toggle={() => dispatch({ type:'CHANGE', k:'export_modal', v:false })}
					promotion={props.promotion}
					promotion_id={props.stage.promotion_id}
					agent_email={owner.agent.Agent.email}
					type="media_entries"
					filename={`multimedia_entries_${props.stage.promotion_id}_${props.stage.id}`}
				/>
			)}

			{(state.export_zip !== false) && (
				<ExportModal
					stage_id={props.stage.id}
					stages={props.stages}
					toggle={() => dispatch({ type:'CHANGE', k:'export_zip', v:false })}
					promotion={props.promotion}
					promotion_id={props.stage.promotion_id}
					agent_email={owner.agent.Agent.email}
					type="zip_files"
					title={`${i18n("Download Type")}: ZIP Files`}
					subtitle={i18n("One or more ZIP files with the multimedia content of the contest will be generated.")}
					filename={`zip_files_${props.stage.promotion_id}_${props.stage.id}`}
				/>
			)}

			{(state.export_multimedia !== false) && (
				<ExportModal
					stage_id={props.stage.id}
					stages={props.stages}
					toggle={() => dispatch({ type:'CHANGE', k:'export_multimedia', v:false })}
					promotion={props.promotion}
					promotion_id={props.stage.promotion_id}
					agent_email={owner.agent.Agent.email}
					type="media_files"
					filename={`multimedia_urls_${props.stage.promotion_id}_${props.stage.id}`}
				/>
			)}
			
			{(state.full_view !== false) && (
				<FloatingLayer
					backButton={() => dispatch({ type:'CHANGE', k:'full_view', v:false })}
				>
					<div class="without_actions">
						<SingleRegisteredEntry
							basename={`stages/categories/${props.stage.id}/`}
							{...state.full_view}
							userAction={userAction}
							actions={false}
						/>
					</div>
				</FloatingLayer>
			)}
		</>
	)
};

const MediaContent = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_description_text = `<h1>${i18n("Submit your entry")}</h1><p>${i18n("Complete all the fields, and remember that you can enter emojis to make your entry stand out and shine.")}</p>`;
	const video_url = (stage.main_video) ? stage.main_video.url : null;

	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={props.nav_active}
					sections={props?.nav_sections ?? []}
				/>

				<Model model={stage} change={change}>
					<MediaUploader
						_class={{first:true}}
						imageField="main_image"
						videoField="main_video"
						image={stage.full_main_image}
						video={video_url}
					>Upload media</MediaUploader>

					<RichPlaceHolder
						placeholder={default_description_text}
						name="main_description"
						has_emojis={true}
					>Introduction message</RichPlaceHolder>

					<InputPlaceHolder
						placeholder="Continue"
						name="main_button"
						autotranslate={false}
					>{i18n("Label for '%s' button").replace('%s', i18n("Continue"))}</InputPlaceHolder>
				</Model>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="landing" menus={props.promoModel.Custom__Menus}>
					<div class={(window.server_type != '2') ? 'gui_withoutEvents' : ''} style={{width:'100%'}}>
						<MediaParticipation
							stage={{Stage:stage}}
							promotion={props.promotion}
							is_builder={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const MediaForm = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion ?? {};

	const media_title = (stage.media_title || stage.media_title_checkbox) ? true : false;
	const media_description = (stage.media_description || stage.media_description_checkbox) ? true : false;

	const main_color = promotion?.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)';
	const colors = (stage.media_colors == undefined) ? main_color : stage.media_colors;

	const text_permitted = stage?.media_text ?? false;
	const url_permitted = stage?.media_url ?? false;
	const is_media_autocontent = (stage.promotion_type_id == stage_promotion_types.MEDIA_AUTOCONTENT.id) ? true : false;

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={props.nav_active}
					sections={props?.nav_sections ?? []}
				/>

				{(!is_media_autocontent) && (
					<article>
						<BuilderSubSectionTitle
							title="Premoderation"
							icon='knk-accept-user'
						/>

						<Model model={stage} change={change}>
							<Checkbox
								name="entry_premoderation"
							>Enable option for admins to pre-moderate the entries before they are published</Checkbox>

							{/* temporally commented until the functionality is done (maybe it'll go in a future branch) */}
							{/*<Checkbox
								name="media_enable_edit"
							>Enable option for users to save their entry and submit it later</Checkbox>*/}
						</Model>
					</article>
				)}

				<MediaTagsPicker
					model={stage}
					change={change}
					pm={props.promoModel}
					stages={props.stages}
					is_media_autocontent={is_media_autocontent}
					intl={props.intl}
				/>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Cover"
						subtitle="Allows participants to set a cover for their entry. This is useful for project-based contests where users upload multiple photos, documents, and videos."
						icon='knk-scene'
					/>

					<Model model={stage} change={change}>
						<Checkbox
							name="media_title_checkbox"
							value={(media_title) ? true : false}
						>Multimedia Gallery Cover</Checkbox>
					</Model>

					{(media_title) && (
						<Model model={stage} change={change}>
							<Input
								name="media_title"
								placeholder="Title of the cover"
								_class={{size:50}}
							>Cover title label</Input>

							<Checkbox
								name="media_description_checkbox"
								value={(media_description) ? true : false}
							>Enable cover description</Checkbox>

							{(media_description) && (
								<Input
									name="media_description"
									placeholder="Description of the album"
									_class={{size:50}}
								>Cover description label</Input>
							)}

							{(media_description) && (
								<Checkbox
									name="media_description_mandatory"
								>Cover description mandatory</Checkbox>
							)}
						</Model>
					)}
				</article>

				{(media_title || text_permitted || url_permitted) && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Colors"
							icon='knk-artist-mode'
						/>

						<Model model={stage} change={change}>
							<ColorPairsSelector
								name="media_colors"
								label_props={{
									tooltip:<Tooltip>Allows users to pick a color for their entries. This color will appear as background color for text-only content and for the cover if it is enabled. You can add up to 5 colors for them to choose from. If you don't add any, the default color of the promotion will be used.</Tooltip>,
								}}
								skip_textcolor={true}
								min_colors={1}
								max_colors={5}
								defs={{background:main_color}}
								value={colors}
							>Available background colors</ColorPairsSelector>
						</Model>
					</article>
				)}
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="landing" menus={props.promoModel.Custom__Menus}>
					<div class={(window.server_type != '2') ? 'gui_withoutEvents' : ''} style={{width:'100%'}}>
						<MediaParticipation
							stage={{ Stage:stage }}
							promotion={props.promoModel.attributes}
							initial_step={2}
							is_builder={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const MediaBuilder = ({ base_route, ...props }) => {
	const basename = `/stages/${props.stage.id}/media`;
	let nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Nomination information", link:`${basename}/main`, icon:'knk-user' },
		{ label:"Multimedia content", link:`${basename}/settings`, icon:'knk-template' },
		{ label:"Other options", link:`${basename}/form`, icon:'knk-view-compressed' },
	];
	return (
		<Router basename={basename} hashType="noslash">
			<Switch>
				<Route exact={true} path="/candidates">
					<MediaCandidates
						change={props.change}
						intl={props.intl}
						isCorporate={props.isCorporate}
						nav_active={`/candidates`}
						promotion={props.promotion}
						promoModel={props.promoModel}
						saveStage={props.saveStage}
						stage={props.stage}
						stages={props.stages}
						stage_indicator={props.stage_indicator}
					/>
				</Route>

				<Route exact={true} path="/main">
					<BuilderMain pm={props.promoModel}>
						<MediaMain
							change={props.change}
							intl={props.intl}
							isCorporate={props.isCorporate}
							nav_active={`/main`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							saveStage={props.saveStage}
							stage={props.stage}
							stage_indicator={props.stage_indicator}
						/>
					</BuilderMain>
				</Route>

				<Route exact={true} path="/settings">
					<BuilderMain pm={props.promoModel}>
						<MediaSettings
							change={props.change}
							intl={props.intl}
							isCorporate={props.isCorporate}
							nav_active={`/settings`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							saveStage={props.saveStage}
							stage={props.stage}
							stage_indicator={props.stage_indicator}
						/>
					</BuilderMain>
				</Route>

				<Route exact={true} path="/content">
					<BuilderMain pm={props.promoModel}>
						<MediaContent
							change={props.change}
							intl={props.intl}
							nav_active={`/content`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							saveStage={props.saveStage}
							stage={props.stage}
							stage_indicator={props.stage_indicator}
						/>
					</BuilderMain>
				</Route>

				<Route exact={true} path="/form">
					<BuilderMain pm={props.promoModel}>
						<MediaForm
							change={props.change}
							intl={props.intl}
							nav_active={`/form`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							saveStage={props.saveStage}
							stage={props.stage}
							stages={props.stages}
							stage_indicator={props.stage_indicator}
						/>
					</BuilderMain>
				</Route>
			</Switch>
		</Router>
	)
}
export default injectIntl(MediaBuilder);